import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { Breadcrumb } from '../Breadcrumb';
import { CountBadge } from '../CountBadge';
import { FolderTabs, type FolderTab } from '../FolderTabs';
import { buildAuditColumns } from '../table/auditColumns';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { ConfirmDialog } from '../ConfirmDialog';
import { IndexListView } from './IndexListView';
import { HistoryLogsModal } from './HistoryLogsModal';
import { IndexFilterRail } from './IndexFilterRail';
import { IndexPagination } from './IndexPagination';
import { IndexSelectionBar } from './IndexSelectionBar';
import { IndexTable } from './IndexTable';
import { IndexToolbar } from './IndexToolbar';
import { composePrerequisites } from './prerequisites';
import { PrerequisitesBanner } from './PrerequisitesBanner';
import type { ColumnDef, IndexPageProps } from './types';
import { useFolderNavFacet } from './useFolderNavFacet';
import { useCardGroups, useHeaderGroups } from './useHeaderGroups';
import { useIndexQuery } from './useIndexQuery';
import { useFilterRail, useIndexViewType } from './useIndexViewType';

/** The `All` tab: no constraint on the nav axis at all, which is not itself a value on it. */
const ALL_VALUES = '__all__';

/**
 * The `None` tab: the axis is constrained to no value, so no row qualifies.
 *
 * It is offered ONLY while that state holds, because it is a state to get out of rather than
 * one to navigate to — the rail is where it is entered, by unticking the last value. The
 * strip still has to be able to show it: `<FolderTabs>` corrects an active key it cannot
 * find to the first visible tab, so leaving this unrepresented would silently snap the axis
 * back to All and undo the reader's selection.
 */
const NO_VALUE = '__none__';

/**
 * The canonical list shell — reference §3.
 *
 * Every module's primary list routes through this. Feature pages configure columns,
 * facets, and the endpoint; they never roll their own layout. Rolling a bespoke list page
 * is drift to migrate on sight.
 */
export function IndexPage<T>({
  module,
  breadcrumb,
  columns,
  headerGroups,
  cardGroups,
  facets,
  extraFacets,
  navSlices,
  initialFacets,
  folderNavFacet = 'status',
  renderTable,
  extraView,
  aboveTable,
  chromeInPanel = false,
  cacheAnsweredQueries = false,
  defaultView = 'list',
  auditEntityTypes,
  auditColumns = true,
  latestUpdate = true,
  listAction = 'created',
  freezeAnchor = false,
  rowKey,
  fetch,
  reloadKey = 0,
  searchPlaceholder = 'Search',
  emptyMessage,
  moduleNoun,
  onCreate,
  createLabel,
  createPermission,
  createDisabledReason,
  prerequisites = [],
  createMenu,
  shortcutMenu,
  shortcutLabel,
  recordFilters,
  recordActions,
  rail,
  rowActions,
  rowQuickActions,
  rowQuickActionStyle,
  rowProgress,
  onRowOpen,
  openPermission,
  onEdit,
  editPermission,
  onDelete,
  deletePermission,
  deleteLabel,
  deleteDisabledReason,
  otherInfo,
  selectable,
  bulkActions,
  onClearData,
  clearDataPermission,
}: IndexPageProps<T>) {
  const { hasPermission } = useAuth();
  const query = useIndexQuery<T>(
    module,
    fetch,
    reloadKey,
    cacheAnsweredQueries,

    /*
      SEEDED from the first slice, because a slice set has no All tab to arrive on.

      Without this the strip drew slice 0 as active while the query carried no constraint at
      all, so every kind was listed under a tab reading Consumables — an Asset sitting in the
      consumables register. The tab was stating an answer nobody had selected.
    */
    /*
      A SLICE SET WINS, because it has no All tab to arrive on and the strip would otherwise
      draw slice 0 as active over an unfiltered list. `initialFacets` is the other half of the
      same idea for a FACET-derived strip, which does have an All tab: a page that opens on a
      narrower question than its own contents says so here. The two are mutually exclusive in
      practice and the dev check below says so rather than letting one silently lose.
    */
    navSlices?.[0].select ?? initialFacets,
  );
  const { viewType, setViewType } = useIndexViewType(module, defaultView, Boolean(extraView));

  /*
    The page's OWN view, when it is the one on screen. It is handed the query the table would be
    fetched with, so it honours the same search, tabs, tiles and rail — a view ignoring them
    would show what the filters on screen say is hidden.
  */
  const extraViewNode =
    viewType === 'extra' && extraView ? extraView.render({ search: query.search, facets: query.facets }) : null;

  /*
    Which COLUMN GROUPS the reader keeps on screen. Persisted per module, like the column
    widths and the view type beside it — an arrangement rather than a filter, so remembering it
    has nothing to get wrong.
  */
  const {
    hidden: hiddenHeaderGroups,
    toggle: toggleHeaderGroup,
    isVisible: isHeaderGroupVisible,
  } = useHeaderGroups(module);

  /*
    Which CARDS the reader keeps on screen — the same mechanism one band up, and the shell owns
    only the preference. What a card is, and what putting one away does to the chrome, is the
    page's: `aboveTable` is handed the hidden set and applies it.
  */
  const { hidden: hiddenCards, toggle: toggleCard } = useCardGroups(module);
  const { open: railOpen, setOpen: setRailOpen } = useFilterRail(module);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelectedKeys(new Set());
  }, [query.items]);

  /*
    Status is always the last data column. A stable partition, not a sort: everything else
    keeps the order the feature page declared, and the status column lands at the end even
    if the page put it in the middle. Enforcing it here is what makes "always" true — the
    alternative is every future page remembering, which is how drift starts.
  */
  /*
    Created / Created by / Latest update are appended by the SHELL, from the `otherInfo`
    every index page already supplies — so no feature page declares them and none can
    render them differently. `<DataTablePanel>` appends the same three; see
    `buildAuditColumns`, which is the one definition both shells read.
  */
  const auditPair: ColumnDef<T>[] = auditColumns
    ? buildAuditColumns(otherInfo).filter((column) => latestUpdate || column.key !== 'updatedAt')
    : [];

  /*
    A column or a facet the caller lacks the key for is removed here, once, before anything
    downstream sees it — so the table, the list view, the totals footer, the column-width
    settings, and the filter rail cannot disagree about what is visible. Resolving it in
    each of those instead is how two surfaces end up showing different things.
  */
  /*
    A SLICE is active when every axis it names matches what the query holds. Comparing the
    whole facet map instead would make a category filter switch the tab off, which is the
    opposite of what a slice is for — the axes it does not name are none of its business.
  */
  const activeSlice = navSlices?.find((slice) =>
    Object.entries(slice.select).every(([axis, values]) => {
      const held = query.facets[axis] ?? [];
      return held.length === values.length && values.every((v) => held.includes(v));
    }),
  );

  const visibleColumns = columns.filter(
    (column) => !column.permission || hasPermission(column.permission),
  );

  /*
    A slice may also decide WHICH COLUMNS make sense for it.

    That is the second half of what a tab does, and the reference's own strip does both: its
    Real Property tab drops every quantity column, because a building has no on-hand and the
    band would be twenty em dashes across.

    Resolved HERE, beside the permission filter, so the table, the list view, the totals footer,
    the column-width settings and the row preview all see one answer. A page filtering its own
    columns per tab is how two surfaces end up showing different things.

    Named keys rather than a predicate, and the slice's ORDER wins: a tab that reorders its
    columns is stating what matters on it.
  */
  const slicedColumns = activeSlice?.columnKeys
    ? activeSlice.columnKeys
        .map((key) => visibleColumns.find((column) => column.key === key))
        .filter((column): column is ColumnDef<T> => column !== undefined)
    : visibleColumns;

  /*
    COLUMN GROUPS the reader has put away, dropped here — in the SHELL, before the table, the
    list view, the row preview or the column-width settings see them.

    That placement is the whole of it: resolving visibility at the render sites would let the
    two views disagree about which columns exist, which is the failure the permission resolve
    one block up already avoids. A banded table's own header runs are built by walking the
    COLUMNS, so a group whose columns are all gone draws no band without anything being told.

    The SORT is the override. A group holding the column the list is ordered by stays whatever
    the stored preference says — a table sorted by something with no header on screen is one
    whose order the reader can neither explain nor undo.
  */
  const sortedKey = query.sort?.split(':')[0] ?? null;

  const groupedColumns = headerGroups
    ? slicedColumns.filter((column) => {
        const group = headerGroups.find((candidate) => candidate.keys.includes(column.key));

        // A column in no declared group is never hideable — the anchor and the record's own
        // identity columns are not a group the reader put away.
        if (!group) return true;

        return isHeaderGroupVisible(
          group.key,
          sortedKey !== null && group.keys.includes(sortedKey),
        );
      })
    : slicedColumns;

  /*
    An axis a SLICE names belongs to the tab strip, and is dropped from the rail.

    A facet-derived strip and its own rail group are two views of one state, which works
    because both can express every state of that axis. A slice strip cannot: a rail group
    leads with `All`, which clears the axis — and a slice taxonomy has no All tab to stand
    for that. The rail can also tick two kinds at once, which no single slice expresses.
    Either one leaves the strip with no honest answer to draw.

    Filtered in the SHELL, so a page cannot declare the contradiction and have it look fine.
  */
  const slicedAxes = new Set((navSlices ?? []).flatMap((slice) => Object.keys(slice.select)));

  const visibleFacets = facets.filter(
    (facet) =>
      !slicedAxes.has(facet.key) && (!facet.permission || hasPermission(facet.permission)),
  );

  /*
    Which axis the folder nav files by. The page declares the default and the READER decides,
    from the filter rail's cog — so the bounds are `visibleFacets`, never `facets`: a stored
    preference must not be able to put a permission-gated axis's values in tabs across the top
    of the table.
  */
  const { facet: navFacet, setFacet: setNavFacet } = useFolderNavFacet(
    module,
    // `null` is "no strip", and the hook only understands an axis — so it is handed the default
    // and the RESULT is discarded below. Passing null through would make every stored
    // preference resolve to nothing on a page that simply has no tabs.
    folderNavFacet ?? 'status',
    visibleFacets.map((facet) => facet.key),
  );

  // The page opted out of the strip entirely — see `folderNavFacet` on the props.
  const folderNavOff = folderNavFacet === null;

  /*
    Status stays last; the audit pair sits before it; and the record's own LIFECYCLE is moved
    to sit IMMEDIATELY BEFORE status — product call, 29 Sep 2026: *display also the approval
    status, beside the "active" status.*

    BESIDE is the instruction read literally, and it is what makes the pair legible: a lifecycle
    declared wherever a page happened to put it sat three or four columns away from the badge it
    qualifies, so a reader scanning for "what is this record" read two answers in two places. It
    lands AFTER the audit pair rather than before, because adjacency to status is the whole
    point and the provenance trio is already fixed.

    ONE per table. A record has one lifecycle, so a second column claiming to be it is two
    answers to one question — `filter` would put both here and neither would be wrong on its
    own, which is exactly the shape that survives review.
  */
  const stageColumns = groupedColumns.filter((column) => column.stage);

  if (import.meta.env.DEV && stageColumns.length > 1) {
    console.error(
      `[IndexPage] ${module} declares ${stageColumns.length} columns as stage:true. A record has one lifecycle — mark one.`,
    );
  }

  const orderedColumns = [
    ...groupedColumns.filter((column) => !column.status && !column.stage),
    ...auditPair,
    ...stageColumns,
    ...groupedColumns.filter((column) => column.status),
  ];

  const selectedRows = query.items.filter((row) => selectedKeys.has(rowKey(row)));

  /*
    The anchor column's click target. Edit is the default, because for a CRUD module
    "open the record" is the edit form; a read-only module supplies `onRowOpen` instead of
    growing a fake Edit. Either way it is wired only when the viewer can actually use it,
    so the darker anchor is never a dead affordance.
  */
  const openRow = onRowOpen ?? onEdit;
  const openRowPermission = onRowOpen ? openPermission : editPermission;
  const canOpenRow = openRow && (!openRowPermission || hasPermission(openRowPermission));

  const isFiltered = query.search.length > 0 || query.activeFilterCount > 0;

  /*
    Multi-select is STANDARD, not opt-in. Every list of records that can be acted on gets the
    checkbox column, in both views, without the feature page asking — a module that quietly
    shipped without one was a list where the same job took twenty clicks instead of two.

    Derived rather than defaulted to `true`, so it stays honest at both ends: a module with
    something to do to a selection (`onDelete`, or its own `bulkActions`) gets it, and one
    with neither does not. That is what keeps the checkbox off the audit trail, which is
    append-only and where a selection could never lead anywhere. A page can still force the
    answer either way by passing `selectable` explicitly.
  */
  const rowsAreSelectable = selectable ?? (onDelete !== undefined || bulkActions !== undefined);

  /*
    Clear data. A pure permission gate, so the entry is HIDDEN rather than disabled when the
    caller lacks the key — a control they can never enable is noise.

    The shell owns the entry, the confirmation and the reload; the page owns only the call,
    because the shell knows no endpoints. That split is what stops one module confirming a
    mass deletion differently from the next.
  */
  const canClearData =
    onClearData !== undefined && (!clearDataPermission || hasPermission(clearDataPermission));

  /*
    What one page of this module holds, in the reader's words — used by the empty state and by
    the Clear data dialog.

    Derived from the module KEY by default, which is right while a key names the records it
    lists: `users` → "users". It is WRONG the moment a key names something other than its
    rows, and `learning-development` is the first: four vocabularies share that key, so the
    derivation produced "Clear all learning development" on a dialog that clears exactly one of
    them. A destructive action that misstates its own scope is the defect the confirm body
    exists to prevent, so the caller can say what it is really clearing.
  */
  const noun = moduleNoun ?? module.replace('-', ' ');

  /*
    The folder nav — a second control over one axis the rail already exposes, on the product
    owner's call: a cut people take constantly is worth one click rather than two through the
    collapsed rail.

    `folderNavFacet` names the DEFAULT axis and is `status`, the cut almost every module
    takes. A module whose readers file by something else points it there instead — Salary
    Grades is read by type, and its status axis stays in the rail with Show deleted.

    **The reader repoints it**, from the filter rail's cog, and that choice is remembered per
    module (`useFolderNavFacet`). Which cut is worth one click rather than two through the
    rail is a property of the person reading rather than of the module: someone staffing posts
    files personnel by employment where someone auditing files them by status, and the page
    cannot know which of them is looking. It is an arrangement and not a filter, which is what
    makes remembering it safe — repointing the strip moves no facet selection, so a filter left
    on the previous axis is still applied and still drawn in the rail.

    Two controls, ONE piece of state. The active tab is DERIVED from `query.facets[key]`
    rather than held beside it — the same reasoning as `showDeleted`. A tab index with its
    own state would let the strip and the rail disagree, and the list can only obey one of
    them.

    `All` clears that axis entirely. On the status axis that is also what makes deleted rows
    eligible, so it moves the settings cog's Show deleted checkbox — the same axis read a
    third way. Pointed anywhere else it moves nothing but itself, because nothing else reads
    that axis.

    The counts come from the server, and they are only honest if that axis is counted against
    every OTHER facet's selection: a tab reading 40 over a list of 38 is the strip lying about
    what the reader is looking at.
  */
  /*
    THE COUNTS, resolved ONCE and read by all four consumers below — the folder strip, the
    slice badges, `aboveTable` and the filter rail.

    A page supplying `extraFacets` is one whose counts come back on a second call, after the
    rows. Merging per AXIS rather than per value is what makes that safe to arrive late: an
    axis the summary answers replaces the list’s copy outright, and one it says nothing about
    keeps whatever the list sent — so an empty `{}` while the summary is in flight leaves the
    strip drawing exactly what it drew before.

    <b>Resolving it at the four call sites instead is how two of them come to disagree</b>,
    which is the same argument the permission filter and the status hoist already make one
    prop along.
  */
  const facetCounts = extraFacets
    ? { ...query.facetCounts, ...extraFacets }
    : query.facetCounts;

  const navFacetLabel = facets.find((facet) => facet.key === navFacet)?.label ?? navFacet;

  // Empty where the page opted out, which is what leaves `navTabs` empty and the strip undrawn.
  const navValues = folderNavOff ? [] : facetCounts[navFacet] ?? [];
  const chosenNavValues = query.facets[navFacet] ?? [];

  // Present but empty: the reader unticked every value. Distinct from never having filtered.
  const noNavValueChosen = navFacet in query.facets && chosenNavValues.length === 0;

  /*
    With the query seeded and the sliced axes kept out of the rail, nothing can move those
    axes but the tabs themselves — so a slice always matches. If one ever does not, the strip
    below falls back to drawing slice 0, which is the exact lie this shouts about: a tab
    claiming a selection the list is not filtered by. Silent in review, so it is loud in dev.
  */
  if (import.meta.env.DEV && navSlices && !activeSlice) {
    console.error(
      `<IndexPage module="${module}">: no nav slice matches the current facets, so the tab ` +
        `strip is about to draw "${navSlices[0].key}" over an unfiltered list. ` +
        `Facets: ${JSON.stringify(query.facets)}`,
    );
  }

  /*
    BOTH SEED THE QUERY and only one can win, so a page passing both has written a landing
    state that silently does not happen. Loud in dev rather than left to be discovered, which
    is the shape every other "two props claim one thing" check here takes.
  */
  if (import.meta.env.DEV && navSlices && initialFacets) {
    console.error(
      `<IndexPage module="${module}">: navSlices and initialFacets both seed the landing ` +
        'query, and the slice wins. A sliced strip has no All tab to arrive on, so it already ' +
        'lands on slice 0 — drop initialFacets.',
    );
  }

  const sliceTabs: FolderTab<string>[] = (navSlices ?? []).map((slice) => {
    /*
      Resolved HERE rather than by the page, because the counts belong to the query and the
      query belongs to the shell. A zero draws nothing — see `NavSlice.badgeFrom`.
    */
    const badge = slice.badgeFrom
      ? (facetCounts[slice.badgeFrom.facet] ?? [])
          .filter((v) => slice.badgeFrom!.values.includes(v.value))
          .reduce((total, v) => total + v.count, 0)
      : 0;

    return {
      key: slice.key,
      label: (
        <>
          {slice.label}
          {badge > 0 ? <CountBadge count={badge} tone="red" /> : null}
        </>
      ),
    };
  });

  /*
    THE DEFAULT STRIP DRAWS NOTHING — product call, 12 Oct 2026.

    `All · Active · Inactive` is the shape the status axis takes on almost every module, and it
    is the one arrangement of this strip that tells a reader nothing: the rail already carries
    the same axis with the same three states, the resting query is already `status: ['active']`,
    and a band of chrome identical on thirty pages stops being read at all.

    **Suppressed only when it is EXACTLY that set.** A status axis carrying a THIRD bucket
    still draws, because then the strip is saying something the reader cannot get from the
    resting state. So does an axis the reader REPOINTED from the
    rail's cog — `navFacet` is the resolved key, so choosing another one brings the strip back,
    which is what makes the cog entry still worth having.

    This is not `folderNavFacet={null}`, which is a PAGE opting out of the strip for good
    (Repository, whose breadcrumb carries the position). This is the shell declining to draw a
    strip that would repeat the rail.
  */
  const navIsDefaultStatus =
    navFacet === 'status' &&
    navValues.length === 2 &&
    navValues.every((value) => value.value === 'active' || value.value === 'inactive');

  const navTabs: FolderTab<string>[] =
    navValues.length > 0 && !navIsDefaultStatus
      ? [
          {
            key: ALL_VALUES,
            /*
              **All carries NO count**, which is the rail's own rule reaching the strip.

              It used to sum the values below it, and that is only the truth where they
              PARTITION the list. They do on status; they do not on an axis that caps itself
              (the audit trail offers its top 20 actions) or drops rows it cannot label (a log
              written outside a request has no method) — and pointing the nav at one of those
              is something a reader can now do from the rail's cog. It read `All 2134` over a
              list of 2,259, which is the strip lying about what is on screen.

              Nothing is lost by dropping it: with the axis unconstrained the number equalled
              the toolbar's record count three inches away, so it was the same value in two
              slots on exactly the surfaces where it was correct.
            */
            label: <>All</>,
          },
          ...navValues.map((value) => ({
            key: value.value,
            label: (
              <>
                {value.label}
                <CountBadge count={value.count} tone="slate" />
              </>
            ),
          })),

          // Only while it is the state, and it always reads zero — that is the whole point.
          ...(noNavValueChosen
            ? [
                {
                  key: NO_VALUE,
                  label: (
                    <>
                      None
                      <CountBadge count={0} tone="slate" />
                    </>
                  ),
                },
              ]
            : []),
        ]
      : [];

  /*
    One value ticked is that tab. The axis unmentioned is All, and every value ticked is also
    All — with the whole axis selected the result set is identical, so claiming otherwise
    would be the strip lying about what the reader is looking at.

    Ticked-to-nothing is its own tab. It is NOT All: All shows every row and this shows none,
    and the rail already draws the difference.
  */
  const activeNavValue = noNavValueChosen
    ? NO_VALUE
    : chosenNavValues.length === 1 && chosenNavValues[0] !== undefined
      ? chosenNavValues[0]
      : ALL_VALUES;

  const rowChrome = {
    module,
    columns: orderedColumns,
    rows: query.items,
    rowKey,
    firstRow: query.firstRow,
    loading: query.loading,
    refreshing: query.refreshing,
    otherInfo,
    rowActions,
    rowQuickActions,
    rowQuickActionStyle,
    onRowOpen: canOpenRow ? openRow : undefined,
    onEdit,
    editPermission,
    onDelete,
    deletePermission,
    deleteLabel,
    deleteDisabledReason,
  };

  /*
    Selection belongs to the list, not to one view of it. Both surfaces get the same props
    and read the same `selectedKeys`, so a selection survives a view switch and the
    selection bar behaves identically on either side. Handing it only to the table was how
    the checkbox column went missing from the list view.
  */
  const selection = {
    selectable: rowsAreSelectable,
    selectedKeys,
    onToggleRow: (key: string) =>
      setSelectedKeys((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    onToggleAll: () =>
      setSelectedKeys((current) =>
        current.size === query.items.length
          ? new Set()
          : new Set(query.items.map(rowKey)),
      ),
  };

  /*
    Catalogs this module's Create form cannot be filled in without, and which of them are
    missing. See `IndexPrerequisite`: an empty REQUIRED picker is not a thin form, it is a form
    that cannot be submitted, and the reader would otherwise find that out only after opening it.

    Composed by `composePrerequisites`, not here, because there are TWO shells that draw this
    banner — this one and the plantilla's deliberate fork. The derivation lives in one module so
    the banner, its heading and the disabled-Create sentence cannot drift apart between them.

    An explicit `createDisabledReason` still wins — that is a state about the records in front
    of the reader, which is a different question from whether the module has been set up at all.
  */
  const composedPrerequisites = composePrerequisites(prerequisites);

  /*
    The ACTIVE SLICE may withhold Create on its own, and it outranks both of the above.

    A slice that crosses two axes can be a tab nothing is registered INTO — the reference's
    Leased & Rented is the case: an item arrives there by its tenure, so a "+" would have to
    invent a kind for it. That is a state about the tab the reader is standing on, which is a
    narrower and more specific claim than either the page's own reason or an unfilled catalog,
    so it is what the reader is told.

    It reaches all three places Create renders, because they are one action — the shell resolves
    the sentence once for exactly that reason.
  */
  const effectiveCreateDisabledReason =
    activeSlice?.createDisabledReason ?? createDisabledReason ?? composedPrerequisites.reason;

  /*
    The chrome, resolved ONCE so it can render above the tabs or inside the panel without a
    second call — two call sites is two chances for them to be handed different facet state.
  */
  const aboveTableChrome = aboveTable
    ? aboveTable({
        hiddenCards,
        counts: facetCounts,
        selected: query.facets,
        selectFacet: query.selectFacet,
        loading: query.loading,
        activeSlice,
      })
    : null;

  /*
    The toolbar, hoisted so it can render in EITHER place without a second copy of it —
    two copies is how one of them grows a prop the other lacks.
  */
  const toolbarNode = rowsAreSelectable && selectedKeys.size > 0 ? (
            <IndexSelectionBar
              module={module}
              selectedCount={selectedKeys.size}
              bulkItems={bulkActions?.(selectedRows) ?? []}
              onCreate={onCreate ? () => onCreate(activeSlice) : undefined}
              createPermission={createPermission}
            createLabel={createLabel}
              createDisabledReason={effectiveCreateDisabledReason}
              createMenu={createMenu}
              onClear={() => setSelectedKeys(new Set())}
            />
          ) : (
          <IndexToolbar
            module={module}
            searchValue={query.searchInput}
            onSearchChange={query.setSearchInput}
            searchPlaceholder={searchPlaceholder}
            total={query.total}
            loading={query.loading}
            activeFilterCount={query.activeFilterCount}
            filterRailOpen={railOpen}
            onToggleFilterRail={() => setRailOpen(!railOpen)}
            onCreate={onCreate ? () => onCreate(activeSlice) : undefined}
            createPermission={createPermission}
            createLabel={createLabel}
            createDisabledReason={effectiveCreateDisabledReason}
            createMenu={createMenu}
            shortcutMenu={shortcutMenu}
            shortcutLabel={shortcutLabel}
            recordFilters={recordFilters}
            recordActions={recordActions}
            viewType={viewType}
            onViewTypeChange={setViewType}
            extraViewLabel={extraView?.label}
            showDeleted={query.showDeleted}
            onShowDeletedChange={query.setShowDeleted}
            /*
              A module declares the axis by declaring the facet. Audit logs have no
              `status` facet, because an append-only trail has nothing to delete.
            */
            supportsDeleted={facets.some((facet) => facet.key === 'status')}
            /*
              The HEADERS section, drawn only where a page declares groups — a section over an
              empty list would be a heading with nothing under it, which reads as chrome that
              failed to load.
            */
            headerGroups={headerGroups}
            hiddenHeaderGroups={hiddenHeaderGroups}
            onToggleHeaderGroup={toggleHeaderGroup}
            cardGroups={cardGroups}
            hiddenCards={hiddenCards}
            onToggleCard={toggleCard}
            onOpenHistory={() => setHistoryOpen(true)}
            onClearData={canClearData ? () => setClearOpen(true) : undefined}
          />
  );

  return (
    // Full-bleed shell: no page title, no outer card, per-panel scrolling.
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      {/*
        Rendered only where a page declares one — see `breadcrumb`. A page reached only from the
        sidebar may leave it off; one reached THROUGH another page may not, because the crumb is
        its only way back.
      */}
      {breadcrumb ? <Breadcrumb segments={breadcrumb} /> : null}

      <div className="flex min-h-0 min-w-0 flex-1">
        {/*
          A module may supply its OWN rail. Repository does — see `<RepositoryNavRail>`: a file
          browser navigates by folder and by view, which is not what a facet rail is for.

          It REPLACES the standard rail rather than sitting beside it, and the standard one is
          untouched: two rails in one column is two places to narrow a list from, and bending
          `<IndexFilterRail>` to also draw folders would put a file browser's navigation into
          the component every other module depends on.
        */}
        {rail ?? (
        <IndexFilterRail
          module={module}
          open={railOpen}
          /*
            THE SAME close the toolbar's filter button makes, so the two cannot disagree about
            what closing the rail means — and the reader's preference is persisted either way.
          */
          onClose={() => setRailOpen(false)}
          facets={visibleFacets}
          counts={facetCounts}
          selected={query.facets}
          /*
            One handler for the whole rail: ticking a value, unticking one, and All are all
            "this axis now holds exactly these values". All is the empty list — the same
            call the status strip's All tab makes, so the two cannot mean different things.
          */
          onSelect={query.selectFacet}
          onClear={query.clearFacet}
          navFacet={navFacet}
          /*
            No strip, no picker — the absent-callback idiom, not a disabled row. Offering the
            choice would be a control that silently changes nothing the reader can see.

            Two ways to have no strip: a SLICED one has no single axis to repoint, and a page
            that passed `folderNavFacet={null}` has no tabs at all.
          */
          onNavFacetChange={navSlices || folderNavOff ? undefined : setNavFacet}
          loading={query.loading}
        />
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/* Rendered HERE unless the page files its chrome inside the folder panel. */}
          {chromeInPanel ? null : toolbarNode}

          {/*
            The table sits in a padded, rounded panel rather than bleeding to the shell edges.
            The padding is on the outer box and the scroll on the inner one, so the rounded
            corners clip the sticky header and footer instead of scrolling with them.

            **`p-4`, and it stays.** It was removed on 14 Sep 2026 to make the panel meet the
            content area's edges; that was meant for the PROFILE view's bleed panel and must
            not reach the index shell. Taking it off here cost the index page two things at
            once: the status folder nav sat hard against the toolbar's `border-b`, colliding
            with the search group, and on a page with no status tabs the panel's own border
            landed directly on the toolbar's, drawing two 1px lines a pixel apart.

            The full-bleed treatment belongs to `<DataTablePanel fill>` inside a profile tab,
            which is a different component — changing it there does not require changing it
            here, and the two must not be conflated again.
          */}
          <div className="flex min-h-0 min-w-0 flex-1 flex-col p-4">
            {/* What the module cannot be USED without, stated before the reader tries. */}
            <PrerequisitesBanner module={module} composed={composedPrerequisites} />

            {/*
              Whole-list chrome. ABOVE the tabs by default; inside the folder panel when the
              page files it there — see `chromeInPanel`.

              Its own wrapper carries the gap, exactly as the prerequisites banner's does. The
              container below deliberately has none, because the folder tabs and the panel must
              touch: the active tab merges into the panel's top line, and a gap there would
              break the one seam the strip is built on.
            */}
            {aboveTableChrome && !chromeInPanel ? (
              <div className="pb-3">{aboveTableChrome}</div>
            ) : null}

            {/*
              The folder nav carries NO ground of its own — the tabs stand directly on
              the shell's slate-50, which is the one surface behind them.

              This departs from the strip's usual three-surface stack (slate-100 behind
              slate-50 tabs behind a white active one), and it holds here because the index
              shell's own ground is already slate-50: an inactive tab reads as flush with the
              page and is defined by its outline, while the active tab is the only white thing
              on the row and lifts forward into the panel it merges with. The stack does not
              invert — white is still frontmost — it just has two of its three tones equal.

              Indented past the panel's corner radius, so the first tab does not sit over the
              curve it is supposed to merge into.
            */}
            {navSlices || navTabs.length > 0 ? (
              navSlices ? (
                <FolderTabs
                  id={`${module}-slices`}
                  ariaLabel="Filter by kind"
                  tabs={sliceTabs}
                  activeKey={activeSlice?.key ?? navSlices[0].key}
                  paddingX="px-3"
                  onChange={(key) => {
                    const slice = navSlices.find((s) => s.key === key);
                    if (!slice) return;

                    // Every axis the slice names is REPLACED; the rest are left alone.
                    for (const [axis, values] of Object.entries(slice.select)) {
                      query.selectFacet(axis, values);
                    }
                  }}
                />
              ) : (
                <FolderTabs
                  id={`${module}-${navFacet}`}
                  ariaLabel={`Filter by ${navFacetLabel.toLowerCase()}`}
                  tabs={navTabs}
                  activeKey={activeNavValue}
                  paddingX="px-3"
                  onChange={(key) =>
                    key === ALL_VALUES
                      ? query.clearFacet(navFacet)
                      : query.selectFacet(navFacet, key === NO_VALUE ? [] : [key])
                  }
                />
              )
            ) : null}

            {/*
              One box, both views. The list view is a divided list inside this frame rather
              than a stack of floating cards, so the panel edge is the only edge either view
              draws.
            */}
            {/*
              `isolate` contains the table's own stacking — the sticky header is `z-20` and
              its pinned cells `z-30`, which without a stacking context here compete with the
              tab strip outside and paint over the active tab's 1px merge. Isolated, those
              indices only rank against each other and the panel as a whole sits below the
              tabs, which is the order the folder metaphor needs.
            */}
            {/*
              CHROME IN THE PANEL — see `chromeInPanel`. The toolbar and the whole-list
              chrome sit INSIDE the folder panel, above a table that scrolls under them, so
              each tab opens onto its own search, its own tiles and its own shortcut bar.

              The panel stops being the scroll container and becomes a flex column: the
              chrome is `shrink-0` and the table takes the rest. Left as one scrolling box
              the chrome would scroll away with the rows, which is the opposite of filing it
              under a tab.
            */}
            {chromeInPanel ? (
              <div className="isolate flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-panel border border-slate-300 bg-white">
                <div className="shrink-0">{toolbarNode}</div>

                {aboveTableChrome ? (
                  <div className="shrink-0 border-b border-slate-200 px-4 py-3">{aboveTableChrome}</div>
                ) : null}

                <div className="modern-scrollbar isolate min-h-0 min-w-0 flex-1 overflow-auto">
                {query.error ? (
                  <ErrorState
                    message={query.error.message}
                    correlationId={query.error.correlationId}
                    onRetry={() => void query.reload()}
                  />
                ) : !query.loading && query.items.length === 0 ? (
                  <EmptyState
                    message={
                      isFiltered
                        ? `No ${noun} match the current search or filters.`
                        : (emptyMessage ?? `No ${noun} yet.`)
                    }
                    action={
                      /*
                        Withheld while Create is disabled for state. The empty state's button is
                        the same action as the toolbar's, so offering it here live while the
                        toolbar's is disabled would be one action in two states on one screen —
                        and this one carries no tooltip to say why.
                      */
                      onCreate && !isFiltered && !effectiveCreateDisabledReason?.trim()
                        ? {
                          label: createLabel ?? 'Create',
                          onClick: () => onCreate(activeSlice),
                          permission: createPermission,
                        }
                        : undefined
                    }
                  />
                ) : extraViewNode ? (
                  extraViewNode
                ) : viewType === 'list' ? (
                  <IndexListView
                    {...rowChrome}
                    /* One flag, both views: a table whose own columns are the creation
                       metadata is also a list that must not lead with it. */
                    provenanceLead={auditColumns}
                    latestUpdate={latestUpdate}
                    {...selection}
                    listAction={listAction}
                    /* LIST ONLY — the table states the same fact in a column of its own.
                       See `IndexPageProps.rowProgress`, where that pairing is the rule. */
                    rowProgress={rowProgress}
                    onRowOpen={canOpenRow ? openRow : undefined}
                  />
                ) : (
                  (() => {
                    const surface = {
                      ...rowChrome,
                      ...selection,
                      sort: query.sort,
                      onToggleSort: query.toggleSort,
                    };
  
                    /*
                      A DEDICATED table gets the same surface `<IndexTable>` does and nothing
                      more — see `renderTable`. The shell keeps everything around it, so the two
                      cannot disagree about which rows are on screen or which are selected.
                    */
                    return renderTable ? (
                      renderTable(surface)
                    ) : (
                      <IndexTable
                        {...surface}
                        freezeAnchor={freezeAnchor}
                      />
                    );
                  })()
                )}
                </div>
              </div>
            ) : (
              <div className="modern-scrollbar isolate min-h-0 min-w-0 flex-1 overflow-auto rounded-panel border border-slate-300 bg-white">
              {query.error ? (
                <ErrorState
                  message={query.error.message}
                  correlationId={query.error.correlationId}
                  onRetry={() => void query.reload()}
                />
              ) : !query.loading && query.items.length === 0 ? (
                <EmptyState
                  message={
                    isFiltered
                      ? `No ${noun} match the current search or filters.`
                      : (emptyMessage ?? `No ${noun} yet.`)
                  }
                  action={
                    /*
                      Withheld while Create is disabled for state. The empty state's button is
                      the same action as the toolbar's, so offering it here live while the
                      toolbar's is disabled would be one action in two states on one screen —
                      and this one carries no tooltip to say why.
                    */
                    onCreate && !isFiltered && !effectiveCreateDisabledReason?.trim()
                      ? { label: 'Create', onClick: onCreate, permission: createPermission }
                      : undefined
                  }
                />
              ) : extraViewNode ? (
                extraViewNode
              ) : viewType === 'list' ? (
                <IndexListView
                  {...rowChrome}
                  /* One flag, both views: a table whose own columns are the creation
                     metadata is also a list that must not lead with it. */
                  provenanceLead={auditColumns}
                  latestUpdate={latestUpdate}
                  {...selection}
                  listAction={listAction}
                  /* LIST ONLY — the table states the same fact in a column of its own.
                     See `IndexPageProps.rowProgress`, where that pairing is the rule. */
                  rowProgress={rowProgress}
                  onRowOpen={canOpenRow ? openRow : undefined}
                />
              ) : (
                (() => {
                  const surface = {
                    ...rowChrome,
                    ...selection,
                    sort: query.sort,
                    onToggleSort: query.toggleSort,
                  };

                  /*
                    A DEDICATED table gets the same surface `<IndexTable>` does and nothing
                    more — see `renderTable`. The shell keeps everything around it, so the two
                    cannot disagree about which rows are on screen or which are selected.
                  */
                  return renderTable ? (
                    renderTable(surface)
                  ) : (
                    <IndexTable
                      {...surface}
                      freezeAnchor={freezeAnchor}
                    />
                  );
                })()
              )}
              </div>
            )}
          </div>

          {/* The page's own view shows the WHOLE list its own way, so a pager over it would page nothing. */}
          {extraViewNode ? null : (
            <IndexPagination
              module={module}
              page={query.page}
              pageCount={query.pageCount}
              firstRow={query.firstRow}
              lastRow={query.lastRow}
              total={query.total}
              loading={query.loading}
              onPageChange={query.setPage}
            />
          )}
        </div>
      </div>

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module={module}
        entityTypes={auditEntityTypes}
      />

      {/*
        The consequence is stated on the form, before the tick — CLAUDE.md, "a consequence
        goes on the form, not on the tick". It says what will happen, that it is reversible,
        and exactly how to reverse it, because a reader deciding whether to do this needs the
        way back in front of them rather than in a runbook.
      */}
      <ConfirmDialog
        open={clearOpen}
        onClose={() => setClearOpen(false)}
        id={`${module}-clear-data-dialog`}
        title={`Clear all ${noun}`}
        danger
        requireConfirm
        // The widest destructive action in the product: both safeguards, always.
        requireCredential
        loading={clearing}
        body={
          <div className="space-y-2">
            <p>
              Every active record in {noun} will be deleted at once.
            </p>
            <p>
              Nothing is erased. Deleted rows stay in the database and come back into view
              through <span className="font-medium text-slate-700">Show deleted</span> in this
              menu, from where each one can be restored.
            </p>
          </div>
        }
        onConfirm={async () => {
          if (!onClearData) return;

          setClearing(true);
          try {
            await onClearData();
            setClearOpen(false);
            // The list on screen is now entirely wrong, so it is refetched rather than
            // patched — the status counts above it moved too.
            await query.reload();
          } finally {
            setClearing(false);
          }
        }}
      />
    </div>
  );
}
